import React, {
  FormEvent,
  useEffect,
  useMemo,
} from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import { FormControl } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import LoadingButton from '../../../components/LoadingButton/LoadingButton';
import {
  WithTranslation,
  withTranslation,
} from 'react-i18next';
import LocationsStore from '../../../stores/locations-store';
import SettingsStore from '../../../stores/settings-store';
import ConnectionStore from '../../../stores/connection-store';
import { OfflineForm } from './OfflineForm';
import LocationDropdown from './LocationDropdown';
import PlantDropdown from './PlantDropdown';
import { LocalStorage } from '../../../utils/local-storage';
import { IndexDB } from '../../../utils/indexDB';
import NewAuthStore from '../../../modules/newAuth/store/new-auth-store';
import logger from '../../../utils/logger';
import batchGoLogo from '../../../../img/batchgo_blk.svg';
import MaterialStore from '../../../stores/material-store';

interface InjectedProps extends WithTranslation {
  newAuthStore: NewAuthStore;
  connectionStore: ConnectionStore;
  settingsStore: SettingsStore;
  materialStore: MaterialStore;
  locationsStore: LocationsStore;
  t: (key: string) => string;
}

interface PlantChoiceProps extends InjectedProps {}

export interface PlantChoiceFormValues {
  locationId: number | null;
  plantId: number | null;
}

const PlantChoice: React.FC<PlantChoiceProps> = ({
  newAuthStore,
  connectionStore,
  settingsStore,
  locationsStore,
  materialStore,
  i18n,
  t,
}) => {
  const {
    locations,
    locationId,
    plantsForCurrentLocation,
    plantId,
  } = locationsStore;

  useEffect(() => {
    const { user } = newAuthStore;

    if (user?.language) {
      i18n.changeLanguage(user.language);
    }

    connectionStore.check();
  }, [newAuthStore, connectionStore, i18n]);

  const handleLocationChange = (e: React.FormEvent<FormControl>) => {
    const newLocationId = Number((e.target as HTMLSelectElement).value);
    locationsStore.setLocationId(newLocationId);

    if (newLocationId) {
      const plantsForLocation = locationsStore.getPlantsForLocationId(newLocationId);

      if (plantsForLocation.length === 1) {
        // Preselect the plant if there is only one
        const firstPlantId = plantsForLocation[0].id;
        locationsStore.setPlantId(firstPlantId);
      } else {
        locationsStore.setPlantId(null);
      }
    }
  };

  const handleOnSubmit = (values: any, actions: any) => {
    actions.setSubmitting(true);
    if (values.plantId) {
      locationsStore.setPlantId(values.plantId);
      initializeStorages(values.plantId);
    }
    if (values.locationId) {
      locationsStore.setLocationId(values.locationId);
    }
    settingsStore.loadConfiguration();
    settingsStore.loadNotificationSettings();
    locationsStore.getDivision();
    (async () => {
      if (connectionStore.isOnline) {
        await newAuthStore.sendOfflineRequests();
      }
      materialStore.loadMaterials();
    })();
    locationsStore.plantChoiceFormSubmitted = true;
  };

  const initializeStorages = (selectedPlantId: string) => {
    logger.log('Initializing IndexDB and LocalStorage for plant:', selectedPlantId);
    newAuthStore.storage = new LocalStorage(`CB-${selectedPlantId}`);
    newAuthStore.storeDB = new IndexDB(`CloudBatchDB-${selectedPlantId}`);
  };

  const { isLoading } = newAuthStore;
  const initialValues: PlantChoiceFormValues = useMemo(() => ({ locationId, plantId }), [locationId, plantId]);
  const { user } = newAuthStore;

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={yup.object().shape({
          plantId: yup.string().required('This field is required!'),
        })}
        onSubmit={handleOnSubmit}
      >
        {({
          handleSubmit,
          values,
          handleChange,
          isSubmitting,
        }) => (
          <div className="plant-choice login fade-in-up">
            <div className="content">
              <img
                className="logo"
                src={batchGoLogo}
                alt="BatchGo"
              />
              <p className="text-center">
                <i className={'fa fa-user'} /> {user?.login}{' '}
                <i className={'fa fa-sign-out logout-btn'} onClick={() => newAuthStore.logout()} />
              </p>
              {!locations.length && connectionStore.isOnline && !isLoading ? (
                <React.Fragment>
                  <h4 className="text-danger text-center">{t('You don\'t have permission to access the application!')}</h4>
                  <p className="text-center">{t('Please contact your administrator!')}</p>
                </React.Fragment>
              ) : (
                <form className="login-form" onSubmit={handleSubmit}>
                  {!locations.length && !connectionStore.isOnline
                    ? (
                      <OfflineForm
                        onChange={handleChange}
                      />
                    )
                    : (
                      <>
                        <LocationDropdown
                          onChange={(e: FormEvent<FormControl>) => {
                            handleChange(e);
                            handleLocationChange(e);
                          }}
                          locations={locations}
                        />
                        {values.locationId !== null ? (
                          <PlantDropdown
                            onChange={handleChange}
                            plants={plantsForCurrentLocation}
                          />
                        ) : null}
                      </>
                    )}
                  <div className="form-actions">
                    <LoadingButton
                      className="btn-block uppercase"
                      id="btn-access"
                      bsStyle="primary"
                      type="submit"
                      label={t('Access')}
                      loadingLabel={t('Loading')}
                      isLoading={isSubmitting}
                      disabled={isSubmitting || !values.plantId}
                    />
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </Formik>
    </div>
  );
};

export default withTranslation()(inject('newAuthStore', 'connectionStore', 'settingsStore', 'locationsStore', 'materialStore')(observer(PlantChoice)));
